import type { MessageKey } from "@src/i18n/locale-catalog";
import type { Translate } from "@src/i18n/translate";

export function AboutContent({
  t,
  version,
  onClose,
}: {
  t: Translate;
  version: string;
  onClose: () => void;
}) {
  return (
    <div className="about-dialog-content">
      <div className="about-dialog-header">
        <div>
          <p className="about-eyebrow">{t("quickReference")}</p>
          <h1 id="about-title">{t("aboutMattriz")}</h1>
        </div>
        <button
          className="about-close"
          type="button"
          onClick={onClose}
          aria-label={t("close")}
          title={t("close")}
        >
          ×
        </button>
      </div>
      <p className="about-intro">{t("intro")}</p>
      <div
        className="control-guide"
        role="table"
        aria-label={t("mattrizControls")}
      >
        <div className="control-guide-header" role="row">
          <span role="columnheader">{t("control")}</span>
          <span role="columnheader">{t("whatItDoes")}</span>
        </div>
        <div role="row">
          <span role="cell">
            <kbd>2D</kbd> <kbd>3D</kbd>
          </span>
          <span role="cell">{t("switchDimensions")}</span>
        </div>
        <GuideRow t={t} control="resetView" description="restoreCamera" />
        <GuideRow t={t} control="apply" description="applyDescription" />
        <div role="row">
          <span role="cell">
            <kbd>{t("pause")}</kbd> <kbd>{t("resume")}</kbd>
          </span>
          <span role="cell">{t("pauseDescription")}</span>
        </div>
        <GuideRow t={t} control="reset" description="resetDescription" />
        <GuideRow t={t} control="method" description="methodDescription" />
        <GuideRow t={t} control="basis" description="basisDescription" />
        <GuideRow t={t} control="grid" description="gridDescription" />
        <div role="row">
          <span className="guide-add-controls" role="cell">
            <GuideAdd label={t("addMatrix")} symbol="M" />
            <GuideAdd label={t("addVector")} symbol="v" />
          </span>
          <span role="cell">{t("addDescription")}</span>
        </div>
      </div>
      <section
        className="interaction-guide expression-guide"
        aria-labelledby="expression-guide-title"
      >
        <h2 id="expression-guide-title">{t("expressions")}</h2>
        <p>{t("expressionsDescription")}</p>
        <dl>
          <div>
            <dt>{t("expressionOperators")}</dt>
            <dd>
              <span className="expression-tokens">
                <code>+</code>
                <code>-</code>
                <code>*</code>
                <code>/</code>
                <code>^</code>
                <code>!</code>
                <code>( )</code>
                <span aria-hidden="true">…</span>
              </span>
              <span>{t("expressionOperatorsDescription")}</span>
            </dd>
          </div>
          <div>
            <dt>{t("expressionFunctions")}</dt>
            <dd>
              <span className="expression-tokens">
                <code>pi</code>
                <code>e</code>
                <code>sqrt(x)</code>
                <code>abs(x)</code>
                <code>sin(x)</code>
                <code>log(x)</code>
                <span aria-hidden="true">…</span>
              </span>
              <span>{t("expressionFunctionsDescription")}</span>
            </dd>
          </div>
          <div>
            <dt>{t("expressionExamples")}</dt>
            <dd>
              <span className="expression-tokens">
                <code>1/2</code>
                <code>2pi</code>
                <code>abs(-3)</code>
                <code>cos(pi/4)</code>
                <code>5!</code>
              </span>
            </dd>
          </div>
        </dl>
      </section>
      <section
        className="interaction-guide"
        aria-labelledby="interaction-guide-title"
      >
        <h2 id="interaction-guide-title">{t("gestures")}</h2>
        <dl>
          <div>
            <dt>{t("reorder")}</dt>
            <dd>{t("reorderDescription")}</dd>
          </div>
          <div>
            <dt>{t("navigate")}</dt>
            <dd>{t("navigateDescription")}</dd>
          </div>
        </dl>
      </section>
      <footer className="about-links" aria-label={t("projectLinks")}>
        <div className="about-link-list">
          <a
            href="https://github.com/JahsehJ/mattriz"
            target="_blank"
            rel="noreferrer"
          >
            GitHub
          </a>
        </div>
        <small className="about-version">v{version}</small>
      </footer>
    </div>
  );
}

function GuideRow({
  t,
  control,
  description,
}: {
  t: Translate;
  control: MessageKey;
  description: MessageKey;
}) {
  return (
    <div role="row">
      <span role="cell">
        <kbd>{t(control)}</kbd>
      </span>
      <span role="cell">{t(description)}</span>
    </div>
  );
}

function GuideAdd({ label, symbol }: { label: string; symbol: string }) {
  return (
    <span
      className="preset-split guide-add-split"
      role="img"
      aria-label={label}
    >
      <span className="equation-add-button preset-main guide-add-button">
        <math aria-hidden="true">
          <mo>+</mo>
          <mi>{symbol}</mi>
        </math>
      </span>
      <span className="preset-menu" aria-hidden="true">
        <span className="preset-toggle" />
      </span>
    </span>
  );
}
